/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

import * as React from "react";
import { OverflowActions } from "../../Shared/OverflowMenu";
import { PLACEMENT, StatefulPopover } from "baseui/popover";
import { StatefulMenu } from "baseui/menu";
import { Button, KIND } from "baseui/button";
import { TOP_ITEM_ZINDEX } from "../../Utils/SiteProps";
import { TetherPlacement } from "baseui/layer";
import { useDexterTheme } from "../../DesignSystem/Theme/ThemeContext";

interface ItemSelectorDropdownProps {
  title: string;
  itemSelectActions: OverflowActions[];
  placement?: TetherPlacement;
  kind?: keyof typeof KIND;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
}

export const ItemSelectorDropdown: React.FC<ItemSelectorDropdownProps> = (
  props
) => {
  const { currentTheme } = useDexterTheme();

  return (
    <StatefulPopover
      overrides={{
        Body: {
          style: {
            zIndex: TOP_ITEM_ZINDEX,
            backgroundColor: currentTheme.colors.dropdownBackground,
            border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
            borderRadius: "4px",
          },
        },
      }}
      placement={props.placement ?? PLACEMENT.bottomRight}
      content={({ close }) => (
        <StatefulMenu
          items={props.itemSelectActions}
          onItemSelect={(selectedItem) => {
            selectedItem.item.onClick();
            close();
          }}
          overrides={{
            List: {
              style: {
                backgroundColor: currentTheme.colors.dropdownBackground,
                border: `none`,
                height: "auto",
                width: currentTheme.dimensions.dropdownWidth,
              },
            },
            Option: {
              style: ({ $isHighlighted }) => ({
                backgroundColor: $isHighlighted
                  ? currentTheme.colors.dropdownItemHoverBackground
                  : "transparent",
                color: currentTheme.colors.dropdownText,
                padding: currentTheme.spacing.sm,
                cursor: "pointer",
              }),
            },
          }}
        />
      )}
    >
      <Button
        startEnhancer={props.startEnhancer}
        kind={props.kind ?? KIND.primary}
        endEnhancer={props.endEnhancer}
        overrides={{
          BaseButton: {
            style: {
              backgroundColor: currentTheme.colors.dropdownBackground,
              color: currentTheme.colors.dropdownText,
              border: `1px solid ${currentTheme.colors.dropdownBorderColor}`,
              borderRadius: "4px",
              width: currentTheme.dimensions.dropdownWidth,
              height: currentTheme.dimensions.button.height,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            },
          },
        }}
      >
        {props.title}
      </Button>
    </StatefulPopover>
  );
};
